import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRequireAuth } from '@/hooks/use-require-auth';
import { listCollections, addItem, removeItem, patchItemCrop, requestExport, getExportJob, PRESET_CROPS, RATIOS } from '@/lib/frames-api';
import { fetchSubmissionThumbnailDataUrl } from '@/lib/submissions-api';
import { BACKEND_URL } from '@/lib/api';
import { ArrowLeft, X, Plus, Download, RotateCw, Image } from 'lucide-react';

export default function FrameCollectionPage() {
  const navigate = useNavigate();
  const { ready } = useRequireAuth();
  const [sp] = useSearchParams();
  const eventId = sp.get('event_id') || '';
  const colId = sp.get('collection_id') || '';

  const [collection, setCollection] = useState(null);
  const [tray, setTray] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [thumbs, setThumbs] = useState({});
  const [selected, setSelected] = useState(null);
  const [job, setJob] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    if (!eventId || !colId) return;
    setLoading(true);
    try {
      const r = await listCollections(eventId);
      const found = (r.collections || []).find(c => c.id === colId);
      if (!found) throw new Error('Collection not found.');
      setCollection(found); setTray(r.tray || []); setError(null);
    } catch (e) { setError(e?.message || 'Failed to load collection.'); }
    finally { setLoading(false); }
  }, [eventId, colId]);

  useEffect(() => { if (ready) load(); }, [ready, load]);

  useEffect(() => {
    if (!collection) return;
    const ids = new Set();
    for (const it of collection.items || []) ids.add(it.submission_id);
    for (const t of tray) ids.add(t.id);
    (async () => {
      for (const id of ids) {
        if (thumbs[id]) continue;
        try { const url = await fetchSubmissionThumbnailDataUrl(id); if (url) setThumbs(p => ({ ...p, [id]: url })); } catch {}
      }
    })();
  }, [collection, tray]); // eslint-disable-line

  async function onPlace(subId) {
    if (!eventId || !colId) return;
    try { const r = await addItem(eventId, colId, subId, PRESET_CROPS['1:1']); setCollection(r.collection); setTray(p => p.filter(t => t.id !== subId)); } catch (e) { window.alert(e?.message); }
  }
  async function onRemove(subId) {
    if (!eventId || !colId) return;
    try { await removeItem(eventId, colId, subId); load(); } catch (e) { window.alert(e?.message); }
  }
  async function onSetRatio(subId, ratio) {
    if (!eventId || !colId) return;
    try { const r = await patchItemCrop(eventId, colId, subId, { ...PRESET_CROPS[ratio] }); setCollection(r.collection); } catch (e) { window.alert(e?.message); }
  }
  async function onExport() {
    if (!eventId || !colId) return;
    setExporting(true); setJob(null);
    try {
      const r = await requestExport(eventId, colId); setJob(r.job);
      const poll = async () => {
        try { const s = await getExportJob(r.job.id); setJob(s.job); if (s.job.state === 'QUEUED' || s.job.state === 'PREPARING') setTimeout(poll, 2000); else setExporting(false); }
        catch { setExporting(false); }
      };
      setTimeout(poll, 1500);
    } catch (e) { setExporting(false); window.alert(e?.message); }
  }
  function onDownload() { if (job?.download_url) window.open(`${BACKEND_URL}${job.download_url}`, '_blank'); }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6" data-testid="frame-collection-screen">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-[#146BFF] font-semibold text-sm mb-4 hover:underline" data-testid="frame-collection-back">
        <ArrowLeft className="w-4 h-4" /> back to collections
      </button>

      {loading ? (
        <div className="flex justify-center py-8" data-testid="frame-collection-loading"><div className="w-8 h-8 border-2 border-[#146BFF] border-t-transparent rounded-full animate-spin" /></div>
      ) : error ? (
        <div className="bg-[#FBE5E5] border border-[#C93434]/20 rounded-lg p-3 text-sm text-[#C93434]">{error}</div>
      ) : collection ? (
        <div className="flex flex-col gap-4">
          <h1 className="text-2xl font-extrabold text-[#0B1B46]" data-testid="frame-collection-name">{collection.name}</h1>
          {collection.description && <p className="text-sm text-[#5B6472]">{collection.description}</p>}
          <div className="flex gap-2">
            <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#EFF5FF] text-[#146BFF]">{collection.item_count} items</span>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#EFF5FF] text-[#146BFF]">v{collection.version}</span>
          </div>

          {/* Export */}
          <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="frame-export-card">
            {!job ? (
              <>
                <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase mb-2">Export</p>
                <p className="text-sm text-[#5B6472] mb-3">Package this collection as a zip of cropped, EXIF-free JPEGs.</p>
                <button onClick={onExport} disabled={collection.item_count === 0 || exporting}
                  className="h-10 px-6 bg-[#146BFF] hover:bg-[#0F55CC] disabled:opacity-50 text-white font-semibold rounded-lg text-sm transition-colors"
                  data-testid="frame-export-btn">{exporting ? 'Preparing…' : 'Export as zip'}</button>
              </>
            ) : (
              <>
                <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase mb-2">Export</p>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${job.state === 'READY' ? 'bg-[#E4F4EC] text-[#1F8A55]' : job.state === 'FAILED' ? 'bg-[#FBE5E5] text-[#C93434]' : 'bg-[#EFF5FF] text-[#146BFF]'}`}>{job.state}</span>
                <p className="text-sm text-[#5B6472] mt-2" data-testid="frame-export-status">{job.state === 'READY' ? `${job.item_count} frames packaged.` : job.state === 'FAILED' ? (job.error_message || 'Export failed.') : 'Preparing your zip…'}</p>
                {job.state === 'READY' && <button onClick={onDownload} className="h-10 px-6 mt-2 bg-[#146BFF] text-white font-semibold rounded-lg text-sm flex items-center gap-2" data-testid="frame-export-download-btn"><Download className="w-4 h-4" /> Download zip</button>}
                <button onClick={() => setJob(null)} className="h-10 px-6 mt-2 bg-white border border-[#E3E8EF] text-[#0B1B46] font-semibold rounded-lg text-sm" data-testid="frame-export-reset-btn">New export</button>
              </>
            )}
          </div>

          {/* Items */}
          <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="frame-items-card">
            <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase mb-3">Placed items</p>
            {(collection.items || []).length === 0 ? (
              <p className="text-sm text-[#5B6472]">Add frame-selected views from the tray below.</p>
            ) : (
              <div className="flex flex-wrap gap-3">
                {(collection.items || []).map(it => (
                  <div key={it.submission_id} onClick={() => setSelected(it.submission_id === selected ? null : it.submission_id)}
                    className={`w-[148px] p-1 rounded-lg border cursor-pointer transition-colors ${selected === it.submission_id ? 'border-[#146BFF] bg-[#EFF5FF]' : 'border-[#E3E8EF] bg-white'}`}
                    data-testid={`frame-item-${it.submission_id}`}>
                    <div className="relative">
                      {thumbs[it.submission_id] ? <img src={thumbs[it.submission_id]} alt="" className="w-[132px] h-[132px] rounded-md object-cover" /> : <div className="w-[132px] h-[132px] rounded-md bg-[#F3F5F8]" />}
                      <span className="absolute top-1 left-1 px-1 py-0.5 bg-black/55 text-white text-[10px] font-extrabold rounded">{it.crop?.ratio || '1:1'}</span>
                    </div>
                    {selected === it.submission_id && (
                      <>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {RATIOS.map(r => (
                            <button key={r} onClick={(e) => { e.stopPropagation(); onSetRatio(it.submission_id, r); }}
                              className={`px-1.5 py-1 rounded text-[10px] font-extrabold border ${it.crop?.ratio === r ? 'bg-[#146BFF] text-white border-[#146BFF]' : 'bg-white text-[#146BFF] border-[#146BFF]'}`}
                              data-testid={`frame-item-${it.submission_id}-ratio-${r}`}>{r}</button>
                          ))}
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); onRemove(it.submission_id); }}
                          className="flex items-center gap-1 mt-1 px-1.5 py-1 rounded bg-[#FEECEC] text-[#C93434] text-[11px] font-bold"
                          data-testid={`frame-item-${it.submission_id}-remove`}><X className="w-3 h-3" /> Remove</button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Tray */}
          <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="frame-tray-card">
            <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase mb-3">Add a frame-selected view</p>
            {tray.length === 0 ? (
              <p className="text-sm text-[#5B6472]">No unplaced frame-selected views right now.</p>
            ) : (
              <div className="flex gap-3 overflow-x-auto pb-1">
                {tray.map(t => (
                  <button key={t.id} onClick={() => onPlace(t.id)}
                    className="w-[120px] flex-shrink-0 bg-white rounded-lg p-1 border border-[#E3E8EF] text-left hover:border-[#146BFF] transition-colors"
                    data-testid={`frame-tray-item-${t.id}`}>
                    {thumbs[t.id] ? <img src={thumbs[t.id]} alt="" className="w-[108px] h-[82px] rounded object-cover" /> : <div className="w-[108px] h-[82px] rounded bg-[#F3F5F8]" />}
                    <p className="text-[11px] font-semibold text-[#0B1B46] truncate mt-1">{t.caption || t.contributor_first_name || 'Untitled'}</p>
                    <span className="flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded bg-[#146BFF] text-white text-[11px] font-bold w-fit"><Plus className="w-3 h-3" /> Add</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
